import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { USER_BIZ_COUNTRY_MAP, USER_COUNTRY_MAP_BIZ } from "@src/config/country";
import type { Option } from "@store/configurations/types";
import { useSerializeCallback } from "@kalodata/tools";
import { message } from "antd";
import { t } from "i18next";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import { useNavigate } from "react-router-dom";
import {
  VideoRemakeRequestQuantity,
  UserProductCategorySource,
  UserProductImageSource,
  UserProductSellingPointSource,
  UserProductUpsertRequestSourceType,
  VideoRemakeRequestInternalModelId,
} from "@apis/generated/openapi";
import {
  createVideoRemake,
  fetchAiAnalyzeImage,
  fetchVideoRemakeCreditPreview,
  type CharacterAssetRecord,
} from "@apis/http/api";
import { buildCreditPreviewIdentity, buildRemakeIdentity } from "@apis/videoRemakeRequest";
import type { BodyOf } from "@apis/http/typedRoute";
import type { VideoSpecsValue } from "@components/VideoSpecs";
import { DEFAULT_VIDEO_SPECS_VALUE } from "@components/VideoSpecs/videoSpecsRules";
import { useAppSelector } from "@hooks/redux";
import { isDevelopmentOrStaging } from "@utils/env";
import { DEFAULT_VIDEO_LANGUAGE } from "@utils/util";
import { useProductForm, type SellingPointsStatus } from "@pages/library/CreateProductEntityModal/useProductForm";
import {
  MAX_PRODUCT_IMAGES,
  type ProductFormState,
  type ProductImageFile,
  type ProductImageResponse,
  type SellingPointItem,
} from "@pages/library/CreateProductEntityModal/types";
import type { SelectedProductPayload } from "./SelectProductModal";
import type { SelectedCharacterPayload } from "./SelectCharacterModal";
import type { ProductOrigin, ReferenceVideoState } from "./types";

export const VIDEO_REMAKE_PROMPT_MAX = 400;

type VideoRemakeRequest = BodyOf<"POST/api/video/remake">;
type VideoRemakeCreditPreviewRequest = BodyOf<"POST/api/video/remake/credit-preview">;
type ImageDescriptionInfo = VideoRemakeRequest["imageInfos"][number];

let savedSlotIdSeq = 0;
const nextSavedSlotUid = () => `vrs-saved-${++savedSlotIdSeq}-${Date.now()}`;
let detailSlotIdSeq = 0;
const nextDetailSlotUid = () => `vrs-detail-${++detailSlotIdSeq}-${Date.now()}`;
let spIdSeq = 0;
const nextSpId = () => `vrs-sp-${++spIdSeq}`;

interface VideoRemakeStudioProviderProps {
  /** 由 VideoRemakeStudioPage 注入，可以来自 Welcome / ReferenceVideoModal / URL query */
  initialReferenceVideo?: ReferenceVideoState | null;
  /** URL 传入的 region 原始值（缩写或全称均可），用于初始化 Target Market */
  initialRegion?: string;
  /** URL 传入的 language 原始值，用于初始化 Language */
  initialLanguage?: string;
  children: ReactNode;
}

export interface VideoRemakeStudioContextValue {
  /* 参考视频 */
  referenceVideo: ReferenceVideoState | null;
  setReferenceVideo: (video: ReferenceVideoState | null) => void;

  /* 商品表单（透传 useProductForm） */
  form: ProductFormState;
  imageFiles: ProductImageFile[];
  sellingPointsStatus: SellingPointsStatus;
  aiAnalyzing: boolean;
  needAiAnalyze: boolean;
  canAiAnalyze: boolean;
  aiAnalyze: () => Promise<void>;
  parsingLink: boolean;
  linkError: string | null;
  setLinkError: (err: string | null) => void;
  updateForm: (patch: Partial<ProductFormState>) => void;
  handleFilesChange: (files: ProductImageFile[]) => void;
  uploadFile: (file: File, ctx: any) => Promise<any>;
  updateCaption: (uid: string, caption: string) => void;
  setSellingPoints: (items: SellingPointItem[]) => void;
  addSellingPoint: (content: string) => void;
  removeSellingPoint: (id: string) => void;
  inferAiSellingPoints: () => Promise<void>;

  /* 商品来源管理 */
  productOrigin: ProductOrigin;
  hasProduct: boolean;
  parseProductLink: (url: string) => Promise<boolean>;
  pickProductFromLibrary: (payload: SelectedProductPayload) => void;
  acceptManualCreated: (detail: any) => void;
  clearProduct: () => void;

  /* 通用字段 */
  sellingCountry: string;
  setSellingCountry: (v: string) => void;
  language: string;
  setLanguage: (v: string) => void;
  customInstructions: string;
  setCustomInstructions: (v: string) => void;

  /* 角色（模特） */
  characterMode: "auto" | "designated";
  setCharacterMode: (mode: "auto" | "designated") => void;
  selectedCharacter: CharacterAssetRecord | null;
  pickCharacter: (payload: SelectedCharacterPayload) => void;
  clearCharacter: () => void;

  /* 底部参数 */
  videoSpecs: VideoSpecsValue;
  setVideoSpecs: (v: VideoSpecsValue) => void;

  /* Save Product Entity */
  showSaveProductEntity: boolean;
  saveProductEntity: boolean;
  setSaveProductEntity: (v: boolean) => void;

  /* Generate */
  generating: boolean;
  creditCost: number | null;
  creditLoading: boolean;
  canGenerate: boolean;
  generate: () => Promise<void>;
}

const VideoRemakeStudioContext = createContext<VideoRemakeStudioContextValue | null>(null);

export const useVideoRemakeStudio = (): VideoRemakeStudioContextValue => {
  const ctx = useContext(VideoRemakeStudioContext);
  if (!ctx) {
    throw new Error("useVideoRemakeStudio must be used within <VideoRemakeStudioProvider>");
  }
  return ctx;
};

/**
 * 把 UserProductResponse（来自 createUserProduct / fetchUserProductDetail / fetchUserProductList）
 * 反序列化为 ProductFormState + ProductImageFile[]，便于 Studio 内联回填。
 */
const detailToForm = (detail: any): { form: ProductFormState; imageFiles: ProductImageFile[] } => {
  const allImages: any[] = [
    ...(detail?.mainImage ? [detail.mainImage] : []),
    ...(Array.isArray(detail?.images) ? detail.images : []),
  ];
  const captions = (detail?.metadata?.imageCaptions as string[]) ?? [];

  const imageFiles: ProductImageFile[] = allImages.slice(0, MAX_PRODUCT_IMAGES).map((img, index) => ({
    uid: nextDetailSlotUid(),
    name: `image-${index}`,
    url: img?.url ?? "",
    response: {
      assetId: img?.assetId ?? undefined,
      description: captions[index] ?? "",
      aiAnalyzed: true,
      source: (img?.source as UserProductImageSource) ?? UserProductImageSource.USER,
    } satisfies ProductImageResponse,
  }));

  const cat = detail?.category ?? {};
  const categoryText = [cat.priCateName, cat.secCateName, cat.terCateName].filter(Boolean).join(" > ");

  const sellingPoints: SellingPointItem[] = (detail?.sellingPoints ?? []).map((sp: any) => ({
    id: nextSpId(),
    content: sp.content ?? "",
    source: sp.source as UserProductSellingPointSource,
    checked: true,
  }));

  return {
    form: {
      sourceType:
        (detail?.sourceType as UserProductUpsertRequestSourceType) ?? UserProductUpsertRequestSourceType.MANUAL,
      sourceProductId: detail?.sourceProductId,
      sourceUrl: detail?.sourceUrl,
      country: detail?.country,
      title: detail?.title ?? "",
      category: categoryText,
      categorySource: cat.source,
      sellingPoints,
    },
    imageFiles,
  };
};

const imageMeta = (f: ProductImageFile): ProductImageResponse => (f.response ?? {}) as ProductImageResponse;

/**
 * 两条「需 AI 打标」原则（对齐 BasicInfo/useNeedAiAnalyzeImages）：
 * - 原则1：图片未经过 AI 检测（aiAnalyzed=false）
 * - 原则2：图片的标签（description）为空
 */
const imageNeedsTag = (f: ProductImageFile): boolean => {
  if (!f.url) return false;
  const meta = imageMeta(f);
  return !meta.aiAnalyzed || !meta.description?.trim();
};

const toImageInfos = (imageFiles: ProductImageFile[]): ImageDescriptionInfo[] =>
  imageFiles.flatMap((f) => {
    const meta = (f.response ?? {}) as ProductImageResponse;
    if (!f.url || typeof meta.assetId !== "number") return [];
    return [
      {
        assetId: meta.assetId,
        imageUrl: f.url,
        description: meta.description ?? "",
      },
    ];
  });

function resolveSellingCountry(raw: string, options: (Option & { name?: string })[]): string | null {
  if (!raw) return null;
  const optValues = options.map((r) => r.name ?? r.value).filter(Boolean);
  const lower = raw.toLowerCase();
  const direct = optValues.find((v) => v.toLowerCase() === lower);
  if (direct) return direct;
  const fullFromCode = USER_COUNTRY_MAP_BIZ[raw.toUpperCase()];
  if (fullFromCode) {
    const match = optValues.find((v) => v.toLowerCase() === fullFromCode.toLowerCase());
    if (match) return match;
  }
  const code = USER_BIZ_COUNTRY_MAP[lower];
  if (code) {
    const full = USER_COUNTRY_MAP_BIZ[code];
    if (full) {
      const match = optValues.find((v) => v.toLowerCase() === full.toLowerCase());
      if (match) return match;
    }
  }
  return null;
}

function resolveLanguage(raw: string, options: Option[]): string | null {
  if (!raw) return null;
  const optValues = options.map((o) => o.value).filter(Boolean);
  const lower = raw.toLowerCase();
  const direct = optValues.find((v) => v.toLowerCase() === lower);
  if (direct) return direct;
  const prefix = optValues.find(
    (v) => v.toLowerCase().startsWith(lower + "-") || lower.startsWith(v.toLowerCase() + "-"),
  );
  return prefix ?? null;
}

export const VideoRemakeStudioProvider = ({
  initialReferenceVideo,
  initialRegion,
  initialLanguage,
  children,
}: VideoRemakeStudioProviderProps) => {
  const navigate = useNavigate();
  const { sellingCountry: sellingCountryOptions, videoLanguage: videoLanguageOptions } = useAppSelector(
    (state) => state.configReducer,
  );

  const [referenceVideo, setReferenceVideo] = useState<ReferenceVideoState | null>(initialReferenceVideo ?? null);

  const productForm = useProductForm({ productId: null, enabled: true });

  const [productOrigin, setProductOrigin] = useState<ProductOrigin>(null);
  /** Library 选中后保留落库的 productId（避免 Generate 时再调 createUserProduct） */
  const [boundProductId, setBoundProductId] = useState<string | null>(null);

  const [sellingCountry, setSellingCountry] = useState<string>("");
  const [language, setLanguage] = useState<string>(DEFAULT_VIDEO_LANGUAGE);
  const [customInstructions, setCustomInstructions] = useState<string>("");

  const [characterMode, setCharacterModeState] = useState<"auto" | "designated">("auto");
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterAssetRecord | null>(null);

  const setCharacterMode = useCallback((mode: "auto" | "designated") => {
    setCharacterModeState(mode);
    if (mode === "auto") setSelectedCharacter(null);
  }, []);

  const [videoSpecs, setVideoSpecs] = useState<VideoSpecsValue>(DEFAULT_VIDEO_SPECS_VALUE);

  const [saveProductEntity, setSaveProductEntity] = useState<boolean>(true);

  const [creditCost, setCreditCost] = useState<number | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);

  /* ===== Reference video & sellingCountry / language 默认值 ===== */
  useEffect(() => {
    if (initialReferenceVideo) setReferenceVideo(initialReferenceVideo);
  }, [initialReferenceVideo]);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (!sellingCountryOptions.length) return;
    initializedRef.current = true;

    const fromRegion = initialRegion ? resolveSellingCountry(initialRegion, sellingCountryOptions) : null;
    const first = sellingCountryOptions[0];
    setSellingCountry(fromRegion ?? first?.name ?? String(first?.value ?? ""));

    if (initialLanguage) {
      const fromLang = resolveLanguage(initialLanguage, videoLanguageOptions);
      if (fromLang) setLanguage(fromLang);
    }
  }, [sellingCountryOptions, videoLanguageOptions, initialRegion, initialLanguage]);

  /* ===== 商品入口操作 ===== */
  const resetForLink = useCallback(() => {
    setBoundProductId(null);
    setSaveProductEntity(true);
    setProductOrigin("link");
  }, []);

  const parseProductLink = useCallback(
    async (url: string) => {
      resetForLink();
      const ok = await productForm.parseLink(url);
      if (!ok) {
        setProductOrigin(null);
      }
      return ok;
    },
    [productForm, resetForLink],
  );

  const acceptManualCreated = useCallback(
    (detail: any) => {
      if (!detail) return;
      const { form: nextForm, imageFiles: nextFiles } = detailToForm(detail);
      productForm.setForm(nextForm);
      productForm.setImageFiles(nextFiles);
      setBoundProductId(String(detail.id));
      // 手动创建已经落库 → Save Product Entity 改为「商品已经在收藏夹」语义，不需要再保存
      setProductOrigin("library-created");
      setSaveProductEntity(false);
    },
    [productForm],
  );

  const pickProductFromLibrary = useCallback(
    (payload: SelectedProductPayload) => {
      // 作废上一次选品可能在途的卖点请求，保证每次用最新结果覆盖
      productForm.resetSellingPoints();
      if (payload.origin === "library-created") {
        const detail = payload.raw;
        const { form: nextForm, imageFiles: nextFiles } = detailToForm(detail);
        productForm.setForm(nextForm);
        productForm.setImageFiles(nextFiles);
        setBoundProductId(String(detail?.id ?? payload.productId));
      } else {
        const product = payload.raw;
        const { enriched } = payload;
        const files: ProductImageFile[] = enriched?.imageFiles.length
          ? enriched.imageFiles
          : product.coverImageUrl
            ? [
                {
                  uid: nextSavedSlotUid(),
                  name: "main",
                  url: product.coverImageUrl,
                  response: {
                    assetId: undefined,
                    description: "",
                    aiAnalyzed: true,
                    source: UserProductImageSource.KALODATA,
                  } satisfies ProductImageResponse,
                },
              ]
            : [];
        productForm.setForm({
          sourceType: UserProductUpsertRequestSourceType.KALODATA,
          sourceProductId: product.id,
          country: enriched?.country ?? product.country,
          title: enriched?.title ?? product.productTitle ?? "",
          category:
            enriched?.category ??
            [product.priCateName, product.secCateName, product.terCateName].filter(Boolean).join(" > "),
          categorySource: UserProductCategorySource.LINK,
          sellingPoints: [],
        });
        productForm.setImageFiles(files);
        setBoundProductId(null); // Saved 不落库，PRD 不展示 Save Product，generate 时不带 productId 也没关系
        // 收藏商品：拉取现成卖点填充 Selling Points 板块
        if (product.id && product.country) {
          void productForm.fetchSavedSellingPoints(String(product.id), product.country);
        }
      }
      setProductOrigin(payload.origin);
      setSaveProductEntity(false);
    },
    [productForm],
  );

  const clearProduct = useCallback(() => {
    productForm.setForm({
      sourceType: UserProductUpsertRequestSourceType.MANUAL,
      title: "",
      category: "",
      sellingPoints: [],
    });
    productForm.setImageFiles([]);
    productForm.setLinkError(null);
    productForm.resetSellingPoints();
    setBoundProductId(null);
    setProductOrigin(null);
    setSaveProductEntity(true);
  }, [productForm]);

  /* ===== 角色（模特）选择 ===== */
  const pickCharacter = useCallback((payload: SelectedCharacterPayload) => {
    setSelectedCharacter(payload.record);
  }, []);

  const clearCharacter = useCallback(() => {
    setSelectedCharacter(null);
  }, []);

  /* ===== Save Product Entity 展示规则（PRD 4.1） ===== */
  const showSaveProductEntity = productOrigin === "link";
  /* ===== Credit preview ===== */
  const creditDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const creditSeqRef = useRef(0);
  useEffect(() => {
    if (!referenceVideo?.videoUrl || !referenceVideo.videoSource || !videoSpecs.resolution) {
      // 清空时也推进序列号，作废可能在途的旧请求，避免其回填覆盖。
      creditSeqRef.current += 1;
      setCreditCost(null);
      return;
    }
    if (creditDebounceRef.current) clearTimeout(creditDebounceRef.current);
    creditDebounceRef.current = setTimeout(async () => {
      const seq = creditSeqRef.current + 1;
      creditSeqRef.current = seq;
      setCreditLoading(true);
      try {
        const request: VideoRemakeCreditPreviewRequest = {
          ...buildCreditPreviewIdentity(referenceVideo),
          resolution: videoSpecs.resolution as VideoRemakeCreditPreviewRequest["resolution"],
        };
        const res: any = await fetchVideoRemakeCreditPreview(request);
        // 已有更新的请求发出，丢弃本次过期响应，避免覆盖最新 creditCost。
        if (creditSeqRef.current !== seq) return;
        const value = typeof res === "number" ? res : (res?.data ?? res?.credits ?? null);
        setCreditCost(typeof value === "number" ? value : null);
      } catch {
        if (creditSeqRef.current !== seq) return;
        setCreditCost(null);
      } finally {
        if (creditSeqRef.current === seq) setCreditLoading(false);
      }
    }, 300);
    return () => {
      if (creditDebounceRef.current) clearTimeout(creditDebounceRef.current);
    };
  }, [referenceVideo, videoSpecs.resolution]);

  /* ===== AI Analyze 打标门槛（对齐 BasicInfo 两条原则） ===== */
  const needAiAnalyzeFiles = useMemo(() => productForm.imageFiles.filter(imageNeedsTag), [productForm.imageFiles]);
  const needAiAnalyze = needAiAnalyzeFiles.length > 0;
  const hasMainImage = !!productForm.imageFiles[0]?.url;
  const canAiAnalyze =
    needAiAnalyze && hasMainImage && !!productForm.form.title.trim() && !!productForm.form.category.trim();

  const aiAnalyze = useSerializeCallback(async () => {
    const files = productForm.imageFiles.filter(imageNeedsTag);
    if (files.length === 0) return;
    if (!productForm.form.title.trim() || !productForm.form.category.trim()) {
      message.warning(t("Please fill product name and category before AI analyze"));
      return;
    }
    try {
      const results = await fetchAiAnalyzeImage({
        title: productForm.form.title,
        category: productForm.form.category,
        imageUrls: files.map((f) => f.url!),
      });
      const ok = (Array.isArray(results) ? results : []).filter((r) => r.success);
      ok.forEach((r) => {
        const target = files.find((f) => f.url === r.imageUrl);
        if (target) productForm.updateCaption(target.uid, r.description ?? "");
      });
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to analyze images"));
    }
  });
  const aiAnalyzing = aiAnalyze.isPending();

  /* ===== Generate ===== */
  const hasProduct = productOrigin !== null;
  const hasProductImageInfo = toImageInfos(productForm.imageFiles).length > 0;

  const generate = useSerializeCallback(async () => {
    const canSubmit =
      !!referenceVideo?.videoUrl &&
      hasProduct &&
      hasProductImageInfo &&
      !!productForm.form.title.trim() &&
      !!productForm.form.category.trim() &&
      !!sellingCountry &&
      !productForm.parsingLink;
    if (!canSubmit || !referenceVideo) return;

    const referenceVideoRequest = buildRemakeIdentity(referenceVideo);

    let productId = boundProductId ?? productForm.form.sourceProductId ?? "";

    // 链接/手动来源且勾选了 Save Product Entity → 先落库
    if (showSaveProductEntity && saveProductEntity) {
      const detail = await productForm.submit();
      if (!detail) return;
      productId = String(detail.id);
      setBoundProductId(productId);
    }

    const categoryNames = productForm.form.category
      .split(">")
      .map((s) => s.trim())
      .filter(Boolean);
    const sellingPoints = productForm.form.sellingPoints
      .filter((sp) => sp.checked && sp.content.trim())
      .map((sp) => sp.content.trim());

    const imageInfos = toImageInfos(productForm.imageFiles);
    const prompt = customInstructions.trim();

    const body: VideoRemakeRequest = {
      ...referenceVideoRequest,
      imageInfos,
      characterAssetId: characterMode === "designated" ? selectedCharacter?.id : undefined,
      aspectRatio: videoSpecs.aspectRatio as VideoRemakeRequest["aspectRatio"],
      quantity: VideoRemakeRequestQuantity.Value1,
      resolution: videoSpecs.resolution as VideoRemakeRequest["resolution"],
      // TODO(leon): 先临时写死，后续再放开
      internalModelId: VideoRemakeRequestInternalModelId.sd2,
      // internalModelId: videoSpecs.internalModelId as VideoRemakeRequest["internalModelId"],
      productTitle: productForm.form.title.trim(),
      productId,
      productCountry: productForm.form.country,
      categoryNames,
      sellingPoints,
      userInputPrompt: prompt || undefined,
      sellingCountry,
      videoLanguage: language as VideoRemakeRequest["videoLanguage"],
    };

    try {
      await createVideoRemake(body);
      message.success(t("Video remake task submitted"));
      navigate("/library");
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to submit video remake task"));
    }
  });

  const generating = generate.isPending();
  const canGenerate =
    !!referenceVideo?.videoUrl &&
    hasProduct &&
    hasProductImageInfo &&
    !needAiAnalyze &&
    !!productForm.form.title.trim() &&
    !!productForm.form.category.trim() &&
    !!sellingCountry &&
    !productForm.parsingLink &&
    productForm.sellingPointsStatus !== "loading" &&
    !generating;

  /* ===== Generate 按钮禁用原因诊断日志（排查用户反馈用，仅 dev/staging 输出，避免生产泄漏表单状态） ===== */
  useEffect(() => {
    if (!isDevelopmentOrStaging()) return;
    if (canGenerate || generating) return;

    const reasons: string[] = [];
    if (!referenceVideo?.videoUrl) reasons.push("❌ 缺少参考视频 (referenceVideo.videoUrl 为空)");
    if (!hasProduct) reasons.push("❌ 未选择或解析商品 (productOrigin 为 null)");
    if (!hasProductImageInfo) reasons.push("❌ 商品缺少有效图片 (需要至少一张带 assetId 的图片)");
    if (needAiAnalyze) reasons.push("❌ 还有图片未完成 AI 打标 (需先点 AI Analyze)");
    if (!productForm.form.title.trim()) reasons.push("❌ 商品名称为空 (form.title)");
    if (!productForm.form.category.trim()) reasons.push("❌ 商品类目为空 (form.category)");
    if (!sellingCountry) reasons.push("❌ 未选择目标市场 (sellingCountry)");
    if (productForm.parsingLink) reasons.push("⏳ 正在解析商品链接 (parsingLink)，解析完成后才可点击");
    if (productForm.sellingPointsStatus === "loading")
      reasons.push("⏳ 正在生成卖点 (sellingPointsStatus=loading)，生成完成后才可点击");

    // 理论上 reasons 不会为空（canGenerate=false 必有命中），兜底防止空分组
    if (reasons.length === 0) return;

    console.groupCollapsed(
      `%c[VideoRemake] Generate 按钮不可点击，共 ${reasons.length} 项未满足：`,
      "color:#e5484d;font-weight:600;",
    );
    reasons.forEach((r) => console.log(r));
    console.log("—— 原始字段快照（可复制反馈给开发）——");
    console.table({
      "参考视频 videoUrl": referenceVideo?.videoUrl ?? "(空)",
      "商品来源 productOrigin": productOrigin ?? "(null)",
      "有效图片数 hasProductImageInfo": hasProductImageInfo,
      "图片总数 imageFiles": productForm.imageFiles.length,
      "商品名称 title": productForm.form.title || "(空)",
      "商品类目 category": productForm.form.category || "(空)",
      "目标市场 sellingCountry": sellingCountry || "(空)",
      "解析链接中 parsingLink": productForm.parsingLink,
      "生成中 generating": generating,
    });
    console.groupEnd();
  }, [
    canGenerate,
    generating,
    referenceVideo?.videoUrl,
    hasProduct,
    hasProductImageInfo,
    needAiAnalyze,
    productOrigin,
    productForm.form.title,
    productForm.form.category,
    productForm.imageFiles.length,
    sellingCountry,
    productForm.parsingLink,
    productForm.sellingPointsStatus,
  ]);

  const value = useMemo<VideoRemakeStudioContextValue>(
    () => ({
      referenceVideo,
      setReferenceVideo,
      form: productForm.form,
      imageFiles: productForm.imageFiles,
      sellingPointsStatus: productForm.sellingPointsStatus,
      aiAnalyzing,
      needAiAnalyze,
      canAiAnalyze,
      aiAnalyze,
      parsingLink: productForm.parsingLink,
      linkError: productForm.linkError,
      setLinkError: productForm.setLinkError,
      updateForm: productForm.updateForm,
      handleFilesChange: productForm.handleFilesChange,
      uploadFile: productForm.uploadFile,
      updateCaption: productForm.updateCaption,
      setSellingPoints: productForm.setSellingPoints,
      addSellingPoint: productForm.addSellingPoint,
      removeSellingPoint: productForm.removeSellingPoint,
      inferAiSellingPoints: productForm.inferAiSellingPoints,
      productOrigin,
      hasProduct,
      parseProductLink,
      pickProductFromLibrary,
      acceptManualCreated,
      clearProduct,
      sellingCountry,
      setSellingCountry,
      language,
      setLanguage,
      customInstructions,
      setCustomInstructions: (v: string) => setCustomInstructions(v.slice(0, VIDEO_REMAKE_PROMPT_MAX)),
      characterMode,
      setCharacterMode,
      selectedCharacter,
      pickCharacter,
      clearCharacter,
      videoSpecs,
      setVideoSpecs,
      showSaveProductEntity,
      saveProductEntity,
      setSaveProductEntity,
      generating,
      creditCost,
      creditLoading,
      canGenerate,
      generate,
    }),
    [
      generating,
      acceptManualCreated,
      aiAnalyze,
      aiAnalyzing,
      needAiAnalyze,
      canAiAnalyze,
      canGenerate,
      clearProduct,
      creditCost,
      creditLoading,
      customInstructions,
      characterMode,
      setCharacterMode,
      selectedCharacter,
      pickCharacter,
      clearCharacter,
      generate,
      hasProduct,
      language,
      parseProductLink,
      pickProductFromLibrary,
      productForm,
      productOrigin,
      referenceVideo,
      saveProductEntity,
      sellingCountry,
      showSaveProductEntity,
      videoSpecs,
    ],
  );

  return <VideoRemakeStudioContext.Provider value={value}>{children}</VideoRemakeStudioContext.Provider>;
};
